import { useTranslation } from '@/i18n/useTranslation';

import { useState } from 'react';
import { ChartSkeleton } from '@/components/common/LoadingSkeleton';
import { InlineError } from '@/components/common/InlineError';
import { EmptyState } from '@/components/common/EmptyState';
import { ScanTrendChart } from '@/components/charts/ScanTrendChart';
import { CorrectionGauge } from '@/components/charts/CorrectionGauge';
import {
  useScanCorrectionProgress,
  useScanCorrections,
  useScanModels,
  useScanSummary,
  useScanTrend,
} from '@/hooks/useScanAnalytics';

export function AnalyticsPage() {
  const { displayValue, locale, t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const filters = {
    date_from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    date_to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  };
  const invalidRange = Boolean(from && to && from > to);
  const summary = useScanSummary(filters, !invalidRange);
  const trend = useScanTrend(filters, 'day', !invalidRange);
  const models = useScanModels(filters, !invalidRange);
  const corrections = useScanCorrections(filters, !invalidRange);
  const progress = useScanCorrectionProgress(filters, !invalidRange);
  const progressCategories = progress.data?.categories ?? [];
  const selectedCategory =
    progressCategories.find((row) => row.category_id === categoryId) ??
    progressCategories.find((row) => row.code === 'bumps_blemishes') ??
    progressCategories[0];
  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <header>
        <h1>{t('Inspection Analytics')}</h1>
        <p>
          {t(
            'Every saved inference is one inspection. Model detections and human correction categories are reported separately.',
          )}
        </p>
      </header>
      <div className="glass-panel cluster">
        <label className="field">
          {t('From')}
          <input
            className="input"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="field">
          {t('To')}
          <input
            className="input"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
      </div>
      {invalidRange && (
        <p role="alert">{t('The start date must not be later than the end date.')}</p>
      )}
      {!invalidRange && (
        <>
          <section className="glass-panel">
            <h2>{t('Scan totals')}</h2>
            {summary.isLoading && <ChartSkeleton />}
            {summary.isError && <InlineError error={summary.error} />}
            {summary.data && (
              <p>
                {t(
                  '{saved} saved inspections · {withDetections} with detections · {detections} detections · {reviewed} reviewed',
                  {
                    saved: summary.data.total_inspections.toLocaleString(locale),
                    withDetections: summary.data.with_detections.toLocaleString(locale),
                    detections: summary.data.total_detections.toLocaleString(locale),
                    reviewed: summary.data.reviewed_inspections.toLocaleString(locale),
                  },
                )}
              </p>
            )}
          </section>
          <section className="glass-panel">
            <h2>{t('Activity over time')}</h2>
            {trend.isLoading && <ChartSkeleton />}
            {trend.isError && <InlineError error={trend.error} />}
            {trend.data?.length === 0 && <EmptyState title={t('No inspections in this range')} />}
            {trend.data && trend.data.length > 0 && <ScanTrendChart data={trend.data} />}
          </section>
          <section className="glass-panel">
            <h2>{t('By model version')}</h2>
            {models.isLoading && <ChartSkeleton />}
            {models.isError && <InlineError error={models.error} />}
            {models.data?.length === 0 && <EmptyState title={t('No model data')} />}
            {models.data && models.data.length > 0 && (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('Model')}</th>
                      <th>{t('Inspections')}</th>
                      <th>{t('With detections')}</th>
                      <th>{t('Detections')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.data.map((row) => (
                      <tr key={row.model_version ?? 'unknown'}>
                        <td>{row.model_version ?? t('Unknown')}</td>
                        <td>{row.total_inspections}</td>
                        <td>{row.with_detections}</td>
                        <td>{row.total_detections}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section className="glass-panel">
            <h2>{t('Correction target progress')}</h2>
            <p>
              {t('Defects corrected to the selected category compared with the configured target.')}
            </p>
            {progress.isLoading && <ChartSkeleton />}
            {progress.isError && <InlineError error={progress.error} />}
            {progress.data?.categories.length === 0 && (
              <EmptyState title={t('No enabled correction categories')} />
            )}
            {progress.data && selectedCategory && (
              <div className="stack">
                <label className="field">
                  {t('Category')}
                  <select
                    className="input"
                    value={selectedCategory.category_id}
                    onChange={(event) => setCategoryId(event.target.value)}
                  >
                    {progress.data.categories.map((row) => (
                      <option key={row.category_id} value={row.category_id}>
                        {displayValue(row.category)}
                      </option>
                    ))}
                  </select>
                </label>
                <CorrectionGauge count={selectedCategory.count} target={progress.data.target} />
              </div>
            )}
          </section>
          <section className="glass-panel">
            <h2>{t('Human correction categories')}</h2>
            <p>
              {t(
                'Current incorrect reviews with a selected category; these are not model predictions.',
              )}
            </p>
            {corrections.isLoading && <ChartSkeleton />}
            {corrections.isError && <InlineError error={corrections.error} />}
            {corrections.data?.length === 0 && (
              <EmptyState title={t('No categorised corrections yet')} />
            )}
            {corrections.data && corrections.data.length > 0 && (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('Group')}</th>
                      <th>{t('Category')}</th>
                      <th>{t('Corrections')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {corrections.data.map((row) => (
                      <tr key={row.category_id}>
                        <td>{displayValue(row.group)}</td>
                        <td>{displayValue(row.category)}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
